import useRewardSettings from "@/hooks/data/useRewardSettings";
import type { SupSingleVaultV3, SupStoredFilterRangeValue } from "@/types";
import { getNetVaultApy } from "@/utils";
import React, { useCallback, useMemo } from "react";

import dictionary from "@/../config/dictionary.config";
import { useStoredData, useStoredDataDispatch } from "@/store";

import GridBarFiltersAtomRange from "@/components/Grid/Bar/Filters/Atom/Range";
import GridBarFiltersAtomHeader from "@/components/Grid/Bar/Filters/Atom/Header";

interface GridBarFiltersApyRangeProps {
  vaults?: SupSingleVaultV3[];
}

export default function GridBarFiltersApyRange({ vaults }: GridBarFiltersApyRangeProps) {
  const rewards = useRewardSettings();

  const { min, max } = useMemo(() => {
    return (
      vaults
        ?.filter(vault => getNetVaultApy(vault, rewards) !== dictionary.statsDefaultValue)
        .reduce(
          (acc, vault) => {
            return {
              min: Math.min(acc.min, parseFloat(getNetVaultApy(vault, rewards).toFixed(2))),
              max: Math.max(acc.max, parseFloat(getNetVaultApy(vault, rewards).toFixed(2))),
            };
          },
          { min: Infinity, max: -Infinity }
        ) ?? { min: 0, max: 100 }
    );
  }, [rewards, vaults]);

  const storedData = useStoredData();
  const storedDispatch = useStoredDataDispatch();

  const filterValue = useMemo(() => {
    const filterValues = storedData.filters.find(storedFilter => storedFilter.name === "apy");
    return (filterValues?.values ?? { min, max }) as SupStoredFilterRangeValue;
  }, [min, max, storedData.filters]);

  const handleFilterChange = useCallback(
    (min: number, max: number) => {
      storedDispatch({
        type: "setFilter",
        filter: {
          name: "apy",
          values: {
            min: min,
            max: max,
          },
        },
      });
    },
    [storedDispatch]
  );

  return (
    <GridBarFiltersAtomHeader name="apy" title="APY Range" caption={`${filterValue.min}%-${filterValue.max}%`} hideCount>
      <GridBarFiltersAtomRange
        fieldType="percent"
        step={0.05}
        max={max}
        from={filterValue.min}
        to={filterValue.max}
        min={min}
        onChange={handleFilterChange}
        formatter={(value: number) => `${value}%`}
      />
    </GridBarFiltersAtomHeader>
  );
}
